import { sanitizeDedication } from '@/lib/books/dedication';
import { bookRequestHeaders } from './request-headers';

export const COMIC_DEDICATION_PLACEHOLDER = 'For Mia, with love from Grandma';
export const COMIC_DEDICATION_HELP = 'Printed on the first page, before the story.';
export const COMIC_DEDICATION_LOCKED_MESSAGE =
  'Your comic is being made, so the dedication can no longer be changed.';

/** A save is needed only when the trimmed text differs from what is stored. */
export function dedicationSaveNeeded(current: string, saved: string): boolean {
  return current.trim() !== saved.trim();
}

/** dedication_saved fires only when a PUT stored a non-empty value that differs from the last saved one. */
export function shouldTrackDedicationSaved(prevSaved: string, stored: string | null): boolean {
  return !!stored && stored.trim() !== '' && stored !== prevSaved;
}

export type DedicationPutResult =
  | { kind: 'saved'; dedication: string }
  | { kind: 'locked' }
  | { kind: 'error'; message: string };

const GENERIC_ERROR = 'We could not save the dedication. Please try again.';

/** PUT the dedication with the book auth headers (Bearer fallback). Never throws. */
export async function putComicDedication(
  bookId: string,
  text: string,
  fetchImpl: typeof fetch = fetch,
): Promise<DedicationPutResult> {
  try {
    const headers = await bookRequestHeaders();
    const r = await fetchImpl(`/api/books/${encodeURIComponent(bookId)}/comic-dedication`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ dedication: text.trim() ? text : null }),
    });
    const data = await r.json().catch(() => ({}));
    if (r.status === 409) return { kind: 'locked' };
    if (!r.ok) return { kind: 'error', message: typeof data?.error === 'string' && data.error ? data.error : GENERIC_ERROR };
    return { kind: 'saved', dedication: typeof data?.dedication === 'string' ? data.dedication : '' };
  } catch {
    return { kind: 'error', message: GENERIC_ERROR };
  }
}

/** Client-side check with the server's own rules (line/char limits), so the parent sees a problem before saving. */
export function dedicationClientError(text: string): string | null {
  const r = sanitizeDedication(text);
  return r.ok ? null : r.error;
}

/** Only a failed save stops checkout. Timeout/no save (null), saved and locked (409) let it through. */
export function shouldBlockCheckout(result: DedicationPutResult | null | undefined): boolean {
  return result?.kind === 'error';
}
